/**
 * useTheme — dark/light mode management.
 *
 * The initial theme is seeded by the inline script in index.html so
 * there is no flash during hydration. This hook reads that initial
 * state, keeps it in sync with a localStorage key, and toggles a
 * `.dark` class on the root element for Tailwind's class-based dark
 * variant.
 */

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "asicrouter:theme";

export type Theme = "light" | "dark";

function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function useTheme(): { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  const apply = useCallback((t: Theme) => {
    const root = document.documentElement;
    root.dataset.theme = t;
    root.classList.toggle("dark", t === "dark");
    window.localStorage.setItem(STORAGE_KEY, t);
  }, []);

  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  // Respond to OS-level changes only if the user has not explicitly
  // picked a theme. Once they click the toggle, the manual choice sticks.
  useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) =>
      setThemeState(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(
    () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
    [],
  );

  return { theme, toggle, setTheme: setThemeState };
}
